import { useState, useEffect } from 'react';
// Remove 'Box' from imports as it's unused
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { sessionApi, Session, CreateSessionData, UpdateSessionData } from '../../api/session.api';
import { format } from 'date-fns';

interface SessionFormDialogProps {
  open: boolean;
  onClose: (refreshNeeded?: boolean) => void;
  session?: Session | null;
  defaultSquadId?: string;
}

export const SessionFormDialog = ({ open, onClose, session, defaultSquadId }: SessionFormDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    sessionType: 'training',
    squadId: defaultSquadId || '',
    startTime: '',
    endTime: '',
    location: '',
    maxCapacity: '',
  });

  useEffect(() => {
    if (session) {
      setFormData({
        title: session.title,
        description: session.description || '',
        sessionType: session.sessionType,
        squadId: session.squadId,
        startTime: format(new Date(session.startTime), "yyyy-MM-dd'T'HH:mm"),
        endTime: format(new Date(session.endTime), "yyyy-MM-dd'T'HH:mm"),
        location: session.location || '',
        maxCapacity: session.maxCapacity?.toString() || '',
      });
    } else if (defaultSquadId) {
      setFormData(prev => ({ ...prev, squadId: defaultSquadId }));
    }
  }, [session, defaultSquadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data: CreateSessionData | UpdateSessionData = {
        title: formData.title,
        description: formData.description || undefined,
        sessionType: formData.sessionType,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        location: formData.location || undefined,
        maxCapacity: formData.maxCapacity ? parseInt(formData.maxCapacity) : undefined,
      };

      if (session) {
        await sessionApi.updateSession(session.id, data);
      } else {
        await sessionApi.createSession({
          ...data,
          squadId: formData.squadId,
        } as CreateSessionData);
      }

      onClose(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save session');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{session ? 'Edit Session' : 'Create New Session'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Session Title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Weekly Training Session"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                select
                label="Session Type"
                value={formData.sessionType}
                onChange={(e) => handleChange('sessionType', e.target.value)}
              >
                <MenuItem value="training">Training</MenuItem>
                <MenuItem value="match">Match</MenuItem>
                <MenuItem value="assessment">Assessment</MenuItem>
                <MenuItem value="event">Event</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., Court 1"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="datetime-local"
                label="Start Time"
                value={formData.startTime}
                onChange={(e) => handleChange('startTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="datetime-local"
                label="End Time"
                value={formData.endTime}
                onChange={(e) => handleChange('endTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Max Capacity (Optional)"
                type="number"
                value={formData.maxCapacity}
                onChange={(e) => handleChange('maxCapacity', e.target.value)}
                placeholder="Leave empty for no limit"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Add any additional details about the session..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {session ? 'Update' : 'Create'} Session
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
